import { useState } from "react";
import { Link } from "expo-router";
import { Button, Checkbox, RefreshIcon, Table } from "@ptsd/uikit";
import type { UseMutateFunction } from "@tanstack/react-query";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Text, View } from "tamagui";

import type { RouterOutputs } from "~/utils/api";
import { api } from "~/utils/api";
import { dateToReadableDMY } from "~/utils/date-to-readable";
import { Pagination } from "~/components/blocks";
import type { UserTableFilterSchema } from "~/components/blocks/filters/schema";
import { useUsersTableFilter } from "~/components/blocks/filters/users-table-filter";
import { RowFloatingPanel } from "~/components/blocks/row-floating-panel";
import { useCalculatePopoverPosition } from "~/hooks";
import { MoveToAnotherGroup } from "./move-to-another-group-dialog";
import { SlicesListDialog } from "./slices-list-dialog";

const LIMIT = 20;

type User = RouterOutputs["person"]["list"]["rows"][number];
const columnHelper = createColumnHelper<User>();

function formatStatus(status: boolean) {
  return status ? "Зареєстрований/на" : "Не зареєстрований/на";
}

type ColumnId =
  | "select"
  | "createdAt"
  | "email"
  | "getUserSlices"
  | "groupName"
  | "isRegistered"
  | "resendInvitation";

const getColumnWidthBasedOnContent = (id: ColumnId) => {
  switch (id) {
    case "select":
      return "$6";
    case "createdAt":
      return 150;
    case "email":
      return undefined;
    case "getUserSlices":
      return 155;
    case "groupName":
      return undefined;
    case "isRegistered":
      return 190;
    case "resendInvitation":
      return 223;
  }
};

const getColumns = ({
  hasPerPersonView,
  resend,
  openSlicesList,
}: {
  hasPerPersonView: boolean;
  resend: UseMutateFunction<number | null, unknown, { email: string }>;
  openSlicesList: (userId: string) => void;
}) => {
  return [
    columnHelper.accessor((row) => ({ id: row.id }), {
      header: ({ table }) => {
        return (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && "indeterminate")
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label="Select all"
          />
        );
      },
      cell: ({ row }) => {
        return (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        );
      },
      enableSorting: false,
      enableHiding: false,
      id: "select",
    }),

    columnHelper.accessor("createdAt", {
      header: "Запрошено",
      cell: (info) => dateToReadableDMY(info.getValue()),
    }),

    columnHelper.accessor("email", {
      header: "Пошта",
      cell: (info) => info.getValue(),
    }),

    columnHelper.accessor((row) => ({ id: row.id }), {
      header: "Список досліджень",
      cell: ({ row }) => {
        return (
          <Button
            width={"100%"}
            height={"$4"}
            variant={"outlined"}
            size={"sm"}
            rounded="lg"
            maxWidth={134}
            paddingHorizontal={"$2"}
            onPress={() => openSlicesList(row.original.id)}
          >
            Переглянути
          </Button>
        );
      },
      enableSorting: false,
      enableHiding: false,
      id: "getUserSlices",
    }),

    columnHelper.accessor("groupName", {
      header: "Група",
      cell: (info) => info.getValue(),
    }),

    columnHelper.accessor("isRegistered", {
      header: "Статус",
      cell: (info) => {
        const row = info.row.original;
        const isActive = row.isActive;
        if (!isActive) {
          return "Деактивований";
        }

        return formatStatus(info.getValue()!);
      },
    }),

    columnHelper.accessor((row) => ({ id: row.id }), {
      header: "Дії",
      cell: ({ row }) => {
        if (row.original.isRegistered && hasPerPersonView) {
          if (!row.original.hasPerPersonDataSharingConsent) {
            return (
              <Button
                width={"100%"}
                height={"$4"}
                variant={"pink"}
                size={"sm"}
                rounded="lg"
                disabled
              >
                Результати
              </Button>
            );
          }

          return (
            <Link asChild href={`/groups/person/${row.original.id}`}>
              <Button
                width={"100%"}
                height={"$4"}
                variant={"pink"}
                size={"sm"}
                rounded="lg"
              >
                Результати
              </Button>
            </Link>
          );
        }

        const isDisabled =
          !!row.original.isRegistered ||
          !row.original.isActive ||
          !row.original.isInvitationResendEnabled;

        return (
          <Button
            width={"100%"}
            height={"$4"}
            variant={"outlined"}
            size={"sm"}
            rounded="lg"
            disabled={isDisabled}
            onPress={() => resend({ email: row.getValue("email") })}
            icon={<RefreshIcon />}
          >
            Запросити знову
          </Button>
        );
      },
      enableSorting: false,
      enableHiding: false,
      id: "resendInvitation",
    }),
  ];
};

const resolveStatusFilter = (status: UserTableFilterSchema["status"]) => {
  switch (status) {
    case "registered":
      return {
        isRegistered: true,
        isActive: undefined,
      };

    case "notregistered":
      return {
        isRegistered: false,
        isActive: undefined,
      };

    case "deactivated":
      return {
        isRegistered: undefined,
        isActive: false,
      };

    default:
      return {
        isRegistered: undefined,
        isActive: undefined,
      };
  }
};

export const UsersTable: React.FC<{ groupId: string }> = ({ groupId }) => {
  const [page, setPage] = useState(0);
  const [slicesListDialogOpen, setSlicesListDialogOpen] = useState(false);
  const [userIdForSliceListDialog, setUserIdForSliceListDialog] = useState<
    string | undefined
  >();
  const { filter } = useUsersTableFilter();
  const [data] = api.person.list.useSuspenseQuery({
    groupIds: groupId === "organization" ? null : [groupId],
    ...resolveStatusFilter(filter.status),
    limit: LIMIT,
    offset: LIMIT * page,
    search: filter.search,
  });
  const [organization] = api.organization.get.useSuspenseQuery();

  const utils = api.useUtils();

  const { mutate } = api.person.resendInvitation.useMutation({
    onSuccess: () => utils.person.list.invalidate(),
  });

  const table = useReactTable({
    data: data.rows,
    columns: getColumns({
      hasPerPersonView: organization.hasPerPersonView,
      resend: mutate,
      openSlicesList: (userId) => {
        setSlicesListDialogOpen(true);
        setUserIdForSliceListDialog(userId);
      },
    }),
    getCoreRowModel: getCoreRowModel(),

    manualPagination: true,
    rowCount: data?.total,
    initialState: {
      pagination: {
        pageIndex: page,
        pageSize: LIMIT,
      },
    },
  });

  const { mutate: setIsActive } = api.person.setIsActive.useMutation({
    onSuccess: () => {
      void utils.person.list.invalidate();
      table.resetRowSelection();
    },
  });

  const selectedRowModel = table.getSelectedRowModel();
  const selectedRows = selectedRowModel.rows.map((row) => row.original);
  const selectedUserIds = selectedRows.map((user) => user.id);
  const selectedUserEmails = selectedRows.map((user) => user.email);

  const isSelectedUserNotTransferable = selectedRows
    .map((user) => user.isTransferable)
    .includes(false);

  const firstSelectedRow = selectedRows[selectedRows.length - 1];
  const { rowRef, popoverPosition } = useCalculatePopoverPosition(
    firstSelectedRow ?? {},
  );

  if (!data) {
    return null;
  }

  const headerGroups = table.getHeaderGroups();
  const tableRows = table.getRowModel().rows;

  const pageIndex = table.getState().pagination.pageIndex;
  const totalPages = table.getPageCount();

  return (
    <>
      <SlicesListDialog
        userId={userIdForSliceListDialog}
        onOpenChange={setSlicesListDialogOpen}
        open={slicesListDialogOpen}
      />
      <View position="relative">
        {selectedRows.length > 0 ? (
          <RowFloatingPanel.Body
            data-id="row-floating-panel"
            position={{ x: 50, y: popoverPosition.y + 98 }}
          >
            <RowFloatingPanel.Button
              data-id="activate-button"
              onPress={() => {
                setIsActive({ isActive: true, userIds: selectedUserIds });
              }}
            >
              Активувати
            </RowFloatingPanel.Button>
            <RowFloatingPanel.Separator />
            <RowFloatingPanel.Button
              data-id="deactivate-button"
              onPress={() => {
                setIsActive({ isActive: false, userIds: selectedUserIds });
              }}
              color={"$darkpink-500"}
            >
              Деактивувати
            </RowFloatingPanel.Button>
            {!isSelectedUserNotTransferable ? (
              <>
                <RowFloatingPanel.Separator />
                <MoveToAnotherGroup
                  resetSelectedRows={() => table.resetRowSelection()}
                  groupId={groupId}
                  userEmails={selectedUserEmails}
                  userIds={selectedUserIds}
                  trigger={
                    <RowFloatingPanel.Button>
                      Перемістити в іншу групу
                    </RowFloatingPanel.Button>
                  }
                />
              </>
            ) : null}
          </RowFloatingPanel.Body>
        ) : null}

        <Table
          alignCells={{ x: "center", y: "center" }}
          alignHeaderCells={{ y: "center", x: "center" }}
          cellHeight="$5.8"
          data-id="users-table"
        >
          <Table.Head data-id="users-table-head">
            {headerGroups.map((headerGroup, index) => {
              return (
                <Table.Row
                  data-id="users-table-header-row"
                  rowLocation={
                    index === 0
                      ? "first"
                      : index === headerGroups.length - 1
                        ? "last"
                        : "middle"
                  }
                  key={headerGroup.id}
                  justifyContent="flex-start"
                >
                  {headerGroup.headers.map((header, index) => {
                    return (
                      <Table.HeaderCell
                        data-id={"users-table-header-cell-" + header.id}
                        backgroundColor="white"
                        key={header.id}
                        justifyContent="flex-start"
                        borderWidth={0}
                        maxWidth={getColumnWidthBasedOnContent(
                          header.id as ColumnId,
                        )}
                        cellLocation={
                          index === 0
                            ? "first"
                            : index === headerGroup.headers.length - 1
                              ? "last"
                              : "middle"
                        }
                      >
                        <Text
                          fontFamily={"$subtitle"}
                          fontSize={"$2"}
                          lineHeight={"$2"}
                          fontWeight={"$5"}
                          color={"$gray-primary-900"}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </Text>
                      </Table.HeaderCell>
                    );
                  })}
                </Table.Row>
              );
            })}
          </Table.Head>

          <Table.Body data-id="users-table-body">
            {tableRows.length === 0 ? (
              <Table.Row
                data-id="users-table-row"
                borderBottomRightRadius={"$1"}
                borderBottomLeftRadius={"$1"}
                backgroundColor={"white"}
                key={1}
              >
                <Table.Cell
                  data-id="users-table-no-data-cell"
                  borderColor={"$gray-200"}
                  width={"100%"}
                  key={1}
                  justifyContent="center"
                >
                  <Text
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    fontFamily={"$subtitle"}
                    fontSize={"$2"}
                    lineHeight={"$2"}
                    color={"$gray-primary-900"}
                  >
                    Немає даних
                  </Text>
                </Table.Cell>
              </Table.Row>
            ) : (
              tableRows?.map((row, index) => {
                return (
                  <Table.Row
                    data-id="users-table-row"
                    ref={(node) => {
                      if (node) rowRef.current[row.original.id] = node;
                    }}
                    backgroundColor={index % 2 === 0 ? "white" : "$gray-50"}
                    selected={row.getIsSelected()}
                    rowLocation={
                      index === 0
                        ? "first"
                        : index === tableRows.length - 1
                          ? "last"
                          : "middle"
                    }
                    key={row.id}
                  >
                    {row.getVisibleCells().map((cell, index) => {
                      return (
                        <Table.Cell
                          data-id={"users-table-cell-" + cell.id}
                          justifyContent="flex-start"
                          maxWidth={getColumnWidthBasedOnContent(
                            cell.column.id as ColumnId,
                          )}
                          cellLocation={
                            index === 0
                              ? "first"
                              : row.getVisibleCells().length - 1 === index
                                ? "last"
                                : "middle"
                          }
                          key={cell.id}
                          flex={1}
                        >
                          <Text
                            width={"100%"}
                            fontFamily={"$subtitle"}
                            lineHeight={"$2"}
                            fontSize={"$2"}
                            fontWeight={"$4"}
                            color={"$gray-primary-900"}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </Text>
                        </Table.Cell>
                      );
                    })}
                  </Table.Row>
                );
              })
            )}
          </Table.Body>
        </Table>
      </View>
      <Pagination
        currentPage={pageIndex + 1}
        onPageChange={(page) => {
          table.setPageIndex(page - 1);
          setPage(page - 1);
        }}
        totalPages={totalPages}
      />
    </>
  );
};
